import { useTranslation } from "react-i18next"

import { FeedCheerButton } from "@/components/crew/feed-cheer-button"
import { FeedDeepLinkButton } from "@/components/crew/feed-deep-link-button"
import { TiktokEmbed } from "@/components/crew/tiktok-embed"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { useInitials } from "@/hooks/use-initials"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { FeedPost } from "@/types/feed"

/**
 * OKS-509 — a single crewmate post in the §10 Crew Feed (UX Spec §2 post card).
 *
 * Layout (top → bottom): member-identity header → TikTok embed + caption →
 * on-post counts → action row (❤ deep-link primary · 🔥 cheer secondary).
 *
 * Member-identity header (§11 STUB): avatar · @handle · rank · XP. The header
 * is DESIGNED to tap through to the §11 member profile, but that route does
 * not exist yet — so v1 renders it as a NON-navigating element (mirrors the
 * OKS-474 `CrewFeedLink` stub precedent). When §11 ships, wrap the header in an
 * Inertia `<Link href={memberProfilePath(author.membership_id)}>`. The
 * `data-membership-id` hook is left in place for that swap.
 *
 * Locked testids: `feed-post-card`, `feed-post-header`, `feed-post-caption`,
 * `feed-post-metrics`.
 */
interface FeedPostCardProps {
  post: FeedPost
}

export function FeedPostCard({ post }: FeedPostCardProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const { author, metrics } = post
  const locale = i18n.language || "fr"

  const handle = author.tiktok_handle ? `@${author.tiktok_handle}` : author.name

  return (
    <Card data-testid="feed-post-card" className="overflow-hidden">
      <CardContent className="flex flex-col gap-3 p-3">
        {/* Member-identity header — §11 stub (non-navigating in v1). */}
        <div
          data-testid="feed-post-header"
          data-membership-id={author.membership_id}
          className="flex items-center gap-2"
        >
          <Avatar className="size-9 shrink-0">
            {author.avatar_url && (
              <AvatarImage src={author.avatar_url} alt={author.name} />
            )}
            <AvatarFallback className="text-xs">
              {initials(author.name || handle)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-semibold">{handle}</span>
            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
              {author.rank_name && (
                <Badge
                  variant="secondary"
                  className="px-1.5 py-0 text-[10px] font-medium"
                >
                  {author.rank_name}
                </Badge>
              )}
              <span className="tabular-nums">
                {t("crew_feed.post.xp", {
                  xp: formatXpNumber(author.xp, locale),
                })}
              </span>
            </span>
          </div>
          {post.is_self && (
            <Badge variant="outline" className="shrink-0 text-[10px]">
              {t("crew_feed.post.you")}
            </Badge>
          )}
        </div>

        {/* TikTok embed (client-side) + caption. */}
        <TiktokEmbed
          videoId={post.tiktok_video_id}
          thumbnailUrl={post.thumbnail_url}
          caption={post.caption}
        />

        {post.caption && (
          <p
            data-testid="feed-post-caption"
            className="text-sm break-words whitespace-pre-line"
          >
            {post.caption}
          </p>
        )}

        {/* On-post counts (latest snapshot — counts only). */}
        <ul
          data-testid="feed-post-metrics"
          className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums"
        >
          <Metric
            label={t("crew_feed.metrics.views")}
            value={metrics.view_count}
          />
          <Metric
            label={t("crew_feed.metrics.likes")}
            value={metrics.like_count}
          />
          <Metric
            label={t("crew_feed.metrics.comments")}
            value={metrics.comment_count}
          />
          <Metric
            label={t("crew_feed.metrics.shares")}
            value={metrics.share_count}
          />
        </ul>

        {/* Action row — primary deep-link + secondary cheer. */}
        <div className="flex items-center gap-2">
          <FeedDeepLinkButton tiktokVideoUrl={post.tiktok_video_url} />
          <FeedCheerButton
            submissionId={post.id}
            cheered={post.cheered}
            cheerCount={post.cheer_count}
            canCheer={post.can_cheer}
            viewerReaction={post.viewer_reaction}
            recipient={{
              name: author.name,
              handle: author.tiktok_handle,
              avatarUrl: author.avatar_url,
              rankName: author.rank_name,
            }}
            postThumbnailUrl={post.thumbnail_url}
            postCaption={post.caption}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <li className={cn("flex items-baseline gap-1")}>
      <span className="text-foreground font-semibold">
        {formatCompactNumber(value)}
      </span>
      <span>{label}</span>
    </li>
  )
}
